import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  Link as LinkIcon,
  FileText,
  Trash2,
  Plus,
  RefreshCw,
  Loader2,
  X,
  CheckCircle2,
  Database,
  Sparkles,
  UploadCloud as CloudUpload,
  Globe,
  FileSpreadsheet,
  FileType2,
  AlertCircle,
  Layers,
  Hash,
  Clock,
  Eye,
  Pencil,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import apiClient, { type ApiResponse } from '@/lib/api';
import { cn, getErrorMessage, getApiErrorMessage } from '@/lib/utils';
import type { PipelineLabel } from '@easyflow/shared';
import { useKBCapabilities, invalidateCapabilitiesCache } from '@/hooks/useKBCapabilities';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { useEmbedderGate, EMBEDDER_STATUS_QUERY_KEY } from '@/hooks/useEmbedderGate';
import { useQueryClient } from '@tanstack/react-query';

const PIPELINE_BADGE: Record<PipelineLabel, { label: string; className: string }> = {
  text_only: { label: 'Pipeline: Testo (OpenAI)', className: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-700' },
  text_plus_ocr: { label: 'Pipeline: Testo + OCR', className: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-700' },
  gemini_multimodal_native: { label: 'Pipeline: Gemini Multimodal nativo', className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-700' },
  gemini_multimodal_pdf_rasterize: { label: 'Pipeline: Gemini Multimodal + PDF rasterizzato', className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700' },
};

type ProcessPhase = 'idle' | 'reading' | 'parsing' | 'chunking' | 'embedding' | 'saving' | 'complete';

const phaseLabels: Record<ProcessPhase, string> = {
  idle: 'In attesa',
  reading: 'Lettura file...',
  parsing: 'Parsing contenuto...',
  chunking: 'Creazione chunks...',
  embedding: 'Generazione embeddings...',
  saving: 'Salvataggio...',
  complete: 'Completato!'
};

const phaseProgress: Record<ProcessPhase, number> = {
  idle: 0,
  reading: 15,
  parsing: 30,
  chunking: 50,
  embedding: 75,
  saving: 90,
  complete: 100
};

interface Document {
  id: string;
  filename: string;
  file_type: string;
  file_size: number;
  chunk_count: number;
  status: string;
  ocr_status?: string;
  error_message?: string;
  created_at: string;
  // Task #1515 — KB metadata schema
  language?: string | null;
  audience?: string[] | null;
  sensitivity?: string | null;
  // Task #1287 — pipeline embedding multimodale Gemini
  embedding_status?: 'queued' | 'processing' | 'completed' | 'failed' | 'dead_letter' | 'skipped' | null;
  embedding_progress_pct?: number | null;
  embedding_total_pages?: number | null;
  embedding_processed_pages?: number | null;
  embedding_failed_pages?: Array<{ page: number; error: string; attempts?: number }> | null;
  embedding_attempts?: number | null;
  embedding_last_error?: string | null;
  // Task #1287 — bypass multimodale Gemini (PDF senza OCR/chunking testuale).
  multimodal_bypass?: boolean;
}

interface URLSource {
  id: string;
  url: string;
  title: string;
  status: string;
  chunk_count: number;
  created_at: string;
  language?: string;
  last_crawled_at?: string;
  processed_at?: string;
  error_message?: string;
  // Task #1515 — KB metadata schema
  audience?: string[] | null;
  sensitivity?: string | null;
}

// Task #1515 — Catalog opzioni metadata KB (caricato all'avvio del componente).
type KbDimension = 'language' | 'audience' | 'sensitivity';
interface KbMetadataOption {
  id: string;
  dimension: KbDimension;
  value: string;
  label: string;
  position: number;
  is_default: boolean;
  is_system: boolean;
}
interface KbMetaState {
  language: string | null;
  audience: string[];
  sensitivity: string | null;
}

interface ChunkPreview {
  filename?: string;
  chunk_count: number;
  total_tokens: number;
  avg_tokens_per_chunk: number;
  chunks: Array<{ index: number; content: string; token_count: number; preview: string }>;
}

interface URLPreview {
  url: string;
  title: string;
  auto_name: string;
  chunk_count: number;
  total_tokens: number;
  avg_tokens_per_chunk: number;
  chunks: Array<{ index: number; content: string; token_count: number; preview: string }>;
}

export interface KnowledgeBaseManagerProps {
  agentId: string;
  agentType?: string;
  compact?: boolean;
  onNavigateToRouting?: () => void;
  canNavigateToRouting?: boolean;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
}

function fileIconFor(filename: string, fileType?: string): { Icon: typeof FileText; tone: string } {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const ft = (fileType || '').toLowerCase();
  if (ext === 'pdf' || ft.includes('pdf')) return { Icon: FileType2, tone: 'text-rose-500 bg-rose-50 dark:bg-rose-900/20' };
  if (['xls', 'xlsx', 'csv'].includes(ext)) return { Icon: FileSpreadsheet, tone: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' };
  if (['doc', 'docx'].includes(ext)) return { Icon: FileText, tone: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20' };
  if (['ppt', 'pptx'].includes(ext)) return { Icon: FileText, tone: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20' };
  if (['md', 'txt'].includes(ext)) return { Icon: FileText, tone: 'text-slate-600 bg-slate-100 dark:bg-slate-800' };
  return { Icon: FileText, tone: 'text-gray-600 bg-gray-100 dark:bg-gray-800' };
}

function faviconFor(url: string): string {
  try {
    const u = new URL(url);
    return `${u.protocol}//${u.hostname}/favicon.ico`;
  } catch {
    return '';
  }
}

function hostnameOf(url: string): string {
  try { return new URL(url).hostname; } catch { return url; }
}

/**
 * Task #1409 — Toggle indicizzazione immagini scrape come chunk multimodali.
 * Visibile solo se l'embedder tenant è "gemini" (gate tecnico: solo Gemini ha
 * embedContent multimodale). PUT su /tenant/embedder-status.
 */
function ScrapeMediaToggle(): JSX.Element | null {
  const { status, refetch } = useEmbedderGate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  if (!status) return null;
  const isGemini = status.current === 'gemini';
  const enabled = isGemini && status.wiki_scrape_index_media === true;
  const tooltip = isGemini
    ? 'Estrae fino a 50 immagini per pagina (cap 200MB sessione). Costo embedding addizionale.'
    : `Funzionalità disponibile solo con embedder Gemini (corrente: ${status.current ?? 'non configurato'}). Cambia embedder dal wizard per attivare.`;

  const onChange = async (next: boolean) => {
    setSaving(true);
    try {
      await apiClient.put('/tenant/embedder-status', { wiki_scrape_index_media: next });
      await queryClient.invalidateQueries({ queryKey: EMBEDDER_STATUS_QUERY_KEY });
      await refetch();
      toast({
        title: next ? 'Immagini scrape: indicizzazione attiva' : 'Immagini scrape: indicizzazione disattivata',
        description: next
          ? 'Le immagini estratte dalle pagine URL verranno indicizzate come chunk multimodali Gemini (max 50 img/pagina, 200MB cumulati).'
          : 'Le immagini estratte dalle pagine URL non saranno più indicizzate (default).',
      });
    } catch (err) {
      toast({
        title: 'Errore aggiornamento toggle',
        description: err instanceof Error ? err.message : 'Errore sconosciuto',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Stato non-Gemini: riga grigia disabilitata + tooltip esplicativo.
  // Mantenere visibile la riga (anziché nasconderla) garantisce
  // discoverability della feature ai tenant non ancora su Gemini.
  if (!isGemini) {
    return (
      <div
        title={tooltip}
        aria-disabled="true"
        className="flex items-center justify-between gap-3 rounded-md border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-900/30 px-3 py-2 opacity-60 cursor-not-allowed"
      >
        <div className="flex flex-col gap-0.5">
          <Label className="text-xs font-medium text-gray-600 dark:text-gray-400">
            Indicizza immagini inline (multimodale Gemini)
          </Label>
          <span className="text-[11px] text-gray-500 dark:text-gray-500">{tooltip}</span>
        </div>
        <Switch checked={false} disabled />
      </div>
    );
  }

  return (
    <div
      title={tooltip}
      className="flex items-center justify-between gap-3 rounded-md border border-amber-200/60 dark:border-amber-900/30 bg-amber-50/40 dark:bg-amber-950/10 px-3 py-2"
    >
      <div className="flex flex-col gap-0.5">
        <Label className="text-xs font-medium text-amber-900 dark:text-amber-200">Indicizza immagini inline (multimodale Gemini)</Label>
        <span className="text-[11px] text-amber-700/80 dark:text-amber-300/70">{tooltip}</span>
      </div>
      <Switch checked={enabled} disabled={saving} onCheckedChange={onChange} />
    </div>
  );
}

export function KnowledgeBaseManager({
  agentId,
  compact = false,
  onNavigateToRouting,
  canNavigateToRouting = false,
}: KnowledgeBaseManagerProps) {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [urls, setUrls] = useState<URLSource[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [chunkPreview, setChunkPreview] = useState<ChunkPreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [docPhase, setDocPhase] = useState<ProcessPhase>('idle');

  const [newUrl, setNewUrl] = useState('');
  const [isScrapingUrl, setIsScrapingUrl] = useState(false);
  const [urlPreview, setUrlPreview] = useState<URLPreview | null>(null);
  const [isUrlPreviewLoading, setIsUrlPreviewLoading] = useState(false);
  const [urlPhase, setUrlPhase] = useState<ProcessPhase>('idle');

  const { capabilities, error: capabilitiesError, refresh: refreshCapabilities } = useKBCapabilities(agentId);
  const firecrawlActive: boolean | null =
    capabilities ? capabilities.firecrawlAvailable : null;
  const [reingestingDocId, setReingestingDocId] = useState<string | null>(null);
  // Polling adattivo: serve uno stato precedente per rilevare transizioni
  // pending → ready e contare i tick "idle". Backoff solo dopo 5 cicli idle
  // consecutivi (3s → 6s → 12s → 24s). Reset su nuovo upload o transizione.
  const pollIntervalRef = useRef<number>(3000);
  const idleTicksRef = useRef<number>(0);
  const prevStatusKeyRef = useRef<string>('');
  const [crawlMode, setCrawlMode] = useState<'single' | 'site'>('single');
  const [crawlMaxPages, setCrawlMaxPages] = useState(10);
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState<{ status: string; pagesFound: number; pagesProcessed: number; totalChunks: number } | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'documents' | 'urls'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [reindexingUrlId, setReindexingUrlId] = useState<string | null>(null);

  // Task #1515 — KB metadata schema state.
  const [kbMetaOptions, setKbMetaOptions] = useState<KbMetadataOption[]>([]);
  const [kbMeta, setKbMeta] = useState<KbMetaState>({ language: null, audience: [], sensitivity: null });
  const [editMetaTarget, setEditMetaTarget] = useState<
    | { kind: 'document'; id: string; label: string; meta: KbMetaState }
    | { kind: 'url'; id: string; label: string; meta: KbMetaState }
    | null
  >(null);
  const [savingEditMeta, setSavingEditMeta] = useState<boolean>(false);

  const basePath = `/knowledge-base/${agentId}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<KbMetadataOption[]>('/kb-metadata-schema');
        if (cancelled || !res.success || !res.data) return;
        setKbMetaOptions(res.data);
        const findDefault = (dim: KbDimension): string | null =>
          res.data!.find((o) => o.dimension === dim && o.is_default)?.value ?? null;
        const findDefaults = (dim: KbDimension): string[] =>
          res.data!.filter((o) => o.dimension === dim && o.is_default).map((o) => o.value);
        setKbMeta({
          language: findDefault('language'),
          audience: findDefaults('audience'),
          sensitivity: findDefault('sensitivity'),
        });
      } catch {
        // Schema non caricabile: fallback ai default backend (auto-applicati su INSERT).
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const kbOptsByDim = useCallback((dim: KbDimension): KbMetadataOption[] =>
    kbMetaOptions.filter((o) => o.dimension === dim).sort((a, b) => a.position - b.position),
    [kbMetaOptions]
  );

  const loadKnowledgeBase = useCallback(async () => {
    try {
      const [docsRes, urlsRes] = await Promise.all([
        apiClient.get<Document[]>(`${basePath}/documents`),
        apiClient.get<URLSource[]>(`${basePath}/urls`)
      ]);
      setDocuments(docsRes.data || []);
      setUrls(urlsRes.data || []);
    } catch (error) {
      console.error('Error loading knowledge base:', error);
    }
  }, [basePath]);

  useEffect(() => {
    if (agentId) {
      loadKnowledgeBase();
    }
  }, [agentId, loadKnowledgeBase]);

  // Task #1307 — polling adattivo: backoff 3s → 6s → 12s → 24s SOLO dopo
  // 5 tick consecutivi senza transizioni di stato. Reset a 3s su:
  //   - nuova fase upload (`docPhase` reading/parsing)
  //   - cambio agentId
  //   - qualsiasi transizione pending → ready/failed/dead_letter rilevata.
  useEffect(() => {
    const statusKey = documents
      .map(d => `${d.id}:${d.ocr_status ?? '-'}:${d.embedding_status ?? '-'}:${d.chunk_count}`)
      .sort()
      .join('|');
    const transitioned = prevStatusKeyRef.current !== '' && prevStatusKeyRef.current !== statusKey;
    prevStatusKeyRef.current = statusKey;

    const hasPending = documents.some(d =>
      d.ocr_status === 'pending' || d.ocr_status === 'processing' ||
      d.embedding_status === 'queued' || d.embedding_status === 'processing'
    );
    if (!hasPending) {
      pollIntervalRef.current = 3000;
      idleTicksRef.current = 0;
      return;
    }
    if (transitioned) {
      pollIntervalRef.current = 3000;
      idleTicksRef.current = 0;
    }
    const interval = pollIntervalRef.current;
    const t = window.setTimeout(() => {
      loadKnowledgeBase();
      idleTicksRef.current += 1;
      if (idleTicksRef.current >= 5) {
        pollIntervalRef.current = Math.min(interval * 2, 24000);
        idleTicksRef.current = 0;
      }
    }, interval);
    return () => window.clearTimeout(t);
  }, [documents, loadKnowledgeBase]);

  useEffect(() => {
    if (docPhase === 'reading' || docPhase === 'parsing') {
      pollIntervalRef.current = 3000;
      idleTicksRef.current = 0;
    }
  }, [docPhase]);

  useEffect(() => {
    pollIntervalRef.current = 3000;
    idleTicksRef.current = 0;
    prevStatusKeyRef.current = '';
  }, [agentId]);

  const handleReingestDoc = useCallback(async (docId: string) => {
    setReingestingDocId(docId);
    try {
      const res = await apiClient.post<{ mode?: string; chunk_count?: number; job_id?: string }>(`${basePath}/documents/${docId}/reingest`, {});
      if (res.success) {
        const mode = res.data?.mode;
        const description =
          mode === 'multimodal_enqueue'
            ? `Job multimodal Gemini accodato (id=${res.data?.job_id ?? '—'}). Gli embedding verranno rigenerati a breve.`
            : mode === 'text_recovered_from_chunks'
              ? `Re-ingestione completata dal testo già indicizzato (${res.data?.chunk_count ?? 0} chunks).`
              : mode === 'manual_reupload_required'
                ? 'Sorgente non disponibile e nessun testo recuperabile. Re-carica il file per rigenerare gli embeddings.'
                : 'Re-ingestione avviata.';
        toast({ title: 'Re-ingest', description });
        await loadKnowledgeBase();
        invalidateCapabilitiesCache(agentId);
        await refreshCapabilities();
      } else {
        const errMsg = (res as { error?: string }).error || 'Re-ingest fallito';
        toast({ title: 'Errore re-ingest', description: errMsg, variant: 'destructive' });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore re-ingest';
      toast({ title: 'Errore re-ingest', description: msg, variant: 'destructive' });
    } finally {
      setReingestingDocId(null);
    }
  }, [agentId, basePath, loadKnowledgeBase, refreshCapabilities, toast]);

  const processSelectedFile = async (file: File) => {
    setSelectedFile(file);
    setChunkPreview(null);
    setIsPreviewLoading(true);
    setDocPhase('reading');

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        setDocPhase('parsing');
        const base64 = (reader.result as string).split(',')[1];

        setDocPhase('chunking');
        const res = await apiClient.post(`${basePath}/documents/preview`, {
          fileBase64: base64,
          filename: file.name
        });

        if (res.success && res.data) {
          setDocPhase('complete');
          setChunkPreview(res.data as ChunkPreview);
        } else {
          setDocPhase('idle');
          toast({ title: 'Errore', description: res.error || 'Preview fallito', variant: 'destructive' });
        }
      } catch (error) {
        setDocPhase('idle');
        toast({ title: 'Errore preview', description: getErrorMessage(error) || 'Errore durante analisi documento', variant: 'destructive' });
      } finally {
        setIsPreviewLoading(false);
      }
    };
    reader.onerror = () => {
      toast({ title: 'Errore', description: 'Impossibile leggere il file', variant: 'destructive' });
      setIsPreviewLoading(false);
      setDocPhase('idle');
      setSelectedFile(null);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processSelectedFile(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processSelectedFile(file);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', selectedFile);
    // Task #1515 — propaga metadata KB scelti dall'utente.
    if (kbMeta.language) formDataUpload.append('language', kbMeta.language);
    if (kbMeta.sensitivity) formDataUpload.append('sensitivity', kbMeta.sensitivity);
    if (kbMeta.audience.length > 0) formDataUpload.append('audience', JSON.stringify(kbMeta.audience));

    setIsLoading(true);
    setDocPhase('embedding');
    try {
      setDocPhase('saving');
      await apiClient.post(`${basePath}/documents`, formDataUpload);
      setDocPhase('complete');
      toast({ title: 'Successo', description: 'Documento caricato e indicizzato!' });
      loadKnowledgeBase();
      invalidateCapabilitiesCache(agentId);
      void refreshCapabilities();
      setTimeout(() => {
        setSelectedFile(null);
        setChunkPreview(null);
        setDocPhase('idle');
      }, 1500);
    } catch (error) {
      setDocPhase('idle');
      toast({ title: 'Errore', description: getApiErrorMessage(error) || 'Errore durante il caricamento', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setChunkPreview(null);
    setDocPhase('idle');
  };

  const handlePreviewUrl = async () => {
    if (!newUrl.trim()) return;

    setIsUrlPreviewLoading(true);
    setUrlPreview(null);
    setUrlPhase('reading');
    try {
      setUrlPhase('parsing');
      const res = await apiClient.post(`${basePath}/urls/preview`, { url: newUrl });
      setUrlPhase('chunking');
      if (res.success && res.data) {
        setUrlPhase('complete');
        setUrlPreview(res.data as URLPreview);
      } else {
        setUrlPhase('idle');
        toast({ title: 'Errore', description: res.error || 'Preview URL fallito', variant: 'destructive' });
      }
    } catch (error) {
      setUrlPhase('idle');
      toast({ title: 'Errore preview URL', description: getErrorMessage(error) || 'Errore durante analisi URL', variant: 'destructive' });
    } finally {
      setIsUrlPreviewLoading(false);
    }
  };

  const handleConfirmScrape = async () => {
    if (!newUrl.trim()) return;

    setIsScrapingUrl(true);
    setUrlPhase('embedding');
    try {
      setUrlPhase('saving');
      // Task #1515 — invia metadata KB con la richiesta scrape.
      await apiClient.post(`${basePath}/urls`, {
        url: newUrl,
        language: kbMeta.language ?? undefined,
        audience: kbMeta.audience,
        sensitivity: kbMeta.sensitivity ?? undefined,
      });
      setUrlPhase('complete');
      toast({ title: 'Successo', description: 'URL aggiunto e indicizzato!' });
      setTimeout(() => {
        setNewUrl('');
        setUrlPreview(null);
        setUrlPhase('idle');
      }, 1500);
      loadKnowledgeBase();
    } catch (error) {
      setUrlPhase('idle');
      toast({ title: 'Errore', description: getApiErrorMessage(error) || 'Errore durante lo scraping', variant: 'destructive' });
    } finally {
      setIsScrapingUrl(false);
    }
  };

  const handleCancelScrape = () => {
    setNewUrl('');
    setUrlPreview(null);
    setUrlPhase('idle');
  };

  const crawlSiteWithProgress = async () => {
    if (!newUrl.trim()) return;

    setIsCrawling(true);
    setCrawlProgress({ status: 'starting', pagesFound: 0, pagesProcessed: 0, totalChunks: 0 });

    try {
      const startRes = await apiClient.post<{ urlId?: string }>(`${basePath}/urls/crawl`, {
        url: newUrl,
        maxPages: crawlMaxPages,
        // Task #1515 — metadata KB applicati a TUTTE le pagine del crawl.
        language: kbMeta.language ?? undefined,
        audience: kbMeta.audience,
        sensitivity: kbMeta.sensitivity ?? undefined,
      });

      // Envelope permissivo: urlId può arrivare in data.urlId o direttamente sull'envelope.
      const startEnvelope = startRes as ApiResponse<{ urlId?: string }> & { urlId?: string };
      const urlId = startEnvelope.data?.urlId || startEnvelope.urlId;
      if (!urlId) {
        throw new Error('Nessun urlId ricevuto dal server');
      }

      const poll = async () => {
        try {
          type CrawlStatus = {
            status?: string;
            pagesFound?: number;
            pagesProcessed?: number;
            totalChunks?: number;
            error?: string;
          };
          const statusRes = await apiClient.get<{
            status?: string;
            pagesFound?: number;
            pagesProcessed?: number;
            totalChunks?: number;
            error?: string;
          }>(`${basePath}/urls/crawl/${urlId}/status`);
          // Envelope permissivo: lo stato può arrivare in data o direttamente sull'envelope.
          const statusEnvelope = statusRes as ApiResponse<CrawlStatus> & CrawlStatus;
          const data: CrawlStatus = statusEnvelope.data || statusEnvelope;
          const status = data.status || 'unknown';

          setCrawlProgress({
            status,
            pagesFound: data.pagesFound || 0,
            pagesProcessed: data.pagesProcessed || 0,
            totalChunks: data.totalChunks || 0
          });

          if (status === 'completed') {
            toast({ title: 'Crawl completato', description: `${data.pagesProcessed || 0} pagine processate, ${data.totalChunks || 0} chunks creati` });
            setNewUrl('');
            setCrawlProgress(null);
            setIsCrawling(false);
            loadKnowledgeBase();
            return;
          }

          if (status === 'failed' || status === 'error') {
            toast({ title: 'Errore crawl', description: data.error || 'Errore durante il crawl del sito', variant: 'destructive' });
            setCrawlProgress(null);
            setIsCrawling(false);
            return;
          }

          setTimeout(poll, 2000);
        } catch (err) {
          toast({ title: 'Errore', description: getErrorMessage(err) || 'Errore durante il polling dello stato', variant: 'destructive' });
          setCrawlProgress(null);
          setIsCrawling(false);
        }
      };

      setTimeout(poll, 2000);
    } catch (error) {
      toast({ title: 'Errore', description: getApiErrorMessage(error) || getErrorMessage(error) || 'Errore avvio crawl', variant: 'destructive' });
      setCrawlProgress(null);
      setIsCrawling(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    try {
      await apiClient.delete(`${basePath}/documents/${docId}`);
      toast({ title: 'Successo', description: 'Documento eliminato' });
      loadKnowledgeBase();
    } catch (error) {
      toast({ title: 'Errore', description: 'Errore durante l\'eliminazione', variant: 'destructive' });
    }
  };

  const handleReindexUrl = async (url: URLSource) => {
    if (reindexingUrlId) return;
    setReindexingUrlId(url.id);
    try {
      await apiClient.delete(`${basePath}/urls/${url.id}`);
      await apiClient.post(`${basePath}/urls`, { url: url.url });
      toast({ title: 'Reindex completato', description: `${url.url} aggiornato` });
      loadKnowledgeBase();
    } catch (error) {
      toast({ title: 'Errore reindex', description: getApiErrorMessage(error) || getErrorMessage(error) || 'Errore reindex', variant: 'destructive' });
    } finally {
      setReindexingUrlId(null);
    }
  };

  const handlePreviewExistingUrl = async (url: URLSource) => {
    setNewUrl(url.url);
    setChunkPreview(null);
    setUrlPreview(null);
    setIsUrlPreviewLoading(true);
    setUrlPhase('parsing');
    try {
      const res = await apiClient.post(`${basePath}/urls/preview`, { url: url.url });
      if (res.success && res.data) {
        setUrlPhase('complete');
        setUrlPreview(res.data as URLPreview);
      } else {
        setUrlPhase('idle');
        toast({ title: 'Errore', description: res.error || 'Anteprima non disponibile', variant: 'destructive' });
      }
    } catch (error) {
      setUrlPhase('idle');
      toast({ title: 'Errore', description: getErrorMessage(error) || 'Anteprima non disponibile', variant: 'destructive' });
    } finally {
      setIsUrlPreviewLoading(false);
    }
  };

  const handleDeleteUrl = async (urlId: string) => {
    try {
      await apiClient.delete(`${basePath}/urls/${urlId}`);
      toast({ title: 'Successo', description: 'URL eliminato' });
      loadKnowledgeBase();
    } catch (error) {
      toast({ title: 'Errore', description: 'Errore durante l\'eliminazione', variant: 'destructive' });
    }
  };

  const totalChunks = useMemo(() =>
    documents.reduce((s, d) => s + Number(d.chunk_count || 0), 0) +
    urls.reduce((s, u) => s + Number(u.chunk_count || 0), 0),
    [documents, urls]
  );

  const docTypeOptions = useMemo(() => {
    const set = new Set<string>();
    documents.forEach(d => {
      const ext = (d.filename.split('.').pop() || '').toLowerCase();
      if (ext) set.add(ext);
    });
    return Array.from(set).sort();
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return documents.filter(d => {
      if (sourceFilter === 'urls') return false;
      if (typeFilter !== 'all') {
        const ext = (d.filename.split('.').pop() || '').toLowerCase();
        if (ext !== typeFilter) return false;
      }
      if (q && !d.filename.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [documents, sourceFilter, typeFilter, searchQuery]);

  const filteredUrls = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return urls.filter(u => {
      if (sourceFilter === 'documents') return false;
      if (typeFilter !== 'all') return false;
      if (q && !(u.url.toLowerCase().includes(q) || (u.title || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [urls, sourceFilter, typeFilter, searchQuery]);

  const activePreview = chunkPreview || urlPreview;
  const previewKind: 'doc' | 'url' | null = chunkPreview ? 'doc' : urlPreview ? 'url' : null;

  return (
    <div className="space-y-5">
      {/* Stats hero */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <FileText className="h-3.5 w-3.5" /> Documenti
          </div>
          <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{documents.length}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Globe className="h-3.5 w-3.5" /> URL
          </div>
          <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{urls.length}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Layers className="h-3.5 w-3.5" /> Chunks tot.
          </div>
          <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{totalChunks}</div>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Database className="h-3.5 w-3.5" /> Stato RAG
          </div>
          <div className="mt-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            {totalChunks > 0 ? 'Indicizzato' : 'Vuoto'}
          </div>
        </div>
      </div>

      {/* Task #1307 — banner warning capabilities (incoerenze tenant↔agent) */}
      {capabilities && capabilities.warnings.length > 0 && (
        <div className="mb-3 space-y-2">
          {capabilities.warnings.map(w => {
            const tone = w.severity === 'error'
              ? 'bg-rose-50 border-rose-300 text-rose-800 dark:bg-rose-950/30 dark:border-rose-700 dark:text-rose-200'
              : w.severity === 'warning'
              ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-950/30 dark:border-amber-700 dark:text-amber-200'
              : 'bg-sky-50 border-sky-300 text-sky-800 dark:bg-sky-950/30 dark:border-sky-700 dark:text-sky-200';
            return (
              <div key={w.code} className={cn('flex items-start gap-2 text-xs rounded-md border px-3 py-2', tone)}>
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span><strong>{w.code}</strong> — {w.message}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className={cn('grid gap-4', activePreview ? 'lg:grid-cols-[1fr_360px]' : 'grid-cols-1')}>
        {/* LEFT column: ingest + lists */}
        <div className="space-y-4 min-w-0">
          {/* Task #1515 — Metadata KB selectors (Lingua / Audience / Sensitività) */}
          {kbMetaOptions.length > 0 && (
            <Card className="border-gray-200 dark:border-gray-700/50">
              <CardHeader className="py-2.5">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-500" />
                  Metadata documenti
                  <span className="text-[11px] font-normal text-gray-500 ml-1">
                    Applicati alle prossime ingestion (file / URL / crawl)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-3 pt-0 pb-3">
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Lingua</Label>
                  <select
                    value={kbMeta.language ?? ''}
                    onChange={(e) => setKbMeta((m) => ({ ...m, language: e.target.value || null }))}
                    className="w-full h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-background px-2 text-sm"
                  >
                    <option value="">—</option>
                    {kbOptsByDim('language').map((o) => (
                      <option key={o.id} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Audience (multi)</Label>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {kbOptsByDim('audience').map((o) => {
                      const checked = kbMeta.audience.includes(o.value);
                      return (
                        <button
                          type="button"
                          key={o.id}
                          onClick={() =>
                            setKbMeta((m) => ({
                              ...m,
                              audience: checked
                                ? m.audience.filter((v) => v !== o.value)
                                : [...m.audience, o.value],
                            }))
                          }
                          className={cn(
                            'text-[11px] px-2 py-1 rounded-full border transition-colors',
                            checked
                              ? 'bg-indigo-500 border-indigo-500 text-white'
                              : 'bg-background border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-400'
                          )}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-gray-500">Sensitività</Label>
                  <select
                    value={kbMeta.sensitivity ?? ''}
                    onChange={(e) => setKbMeta((m) => ({ ...m, sensitivity: e.target.value || null }))}
                    className="w-full h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-background px-2 text-sm"
                  >
                    <option value="">—</option>
                    {kbOptsByDim('sensitivity').map((o) => (
                      <option key={o.id} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Hero drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => !selectedFile && fileInputRef.current?.click()}
            className={cn(
              'relative rounded-xl border-2 border-dashed transition-all cursor-pointer overflow-hidden',
              'bg-gradient-to-br from-sky-50/60 via-white to-purple-50/60 dark:from-sky-950/30 dark:via-gray-900 dark:to-purple-950/30',
              isDragging
                ? 'border-sky-500 ring-2 ring-sky-300 dark:ring-sky-600 scale-[1.01]'
                : 'border-gray-300 dark:border-gray-700 hover:border-sky-400',
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={capabilities ? capabilities.acceptedExtensions.join(',') : undefined}
              onChange={handleFileSelect}
              disabled={isLoading || isPreviewLoading || !capabilities}
              className="hidden"
            />
            <div className="px-6 py-8 sm:py-10 flex flex-col items-center text-center">
              <motion.div
                animate={isDragging ? { y: -4, scale: 1.05 } : { y: 0, scale: 1 }}
                className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-purple-500 flex items-center justify-center shadow-lg shadow-sky-200 dark:shadow-none"
              >
                <CloudUpload className="h-7 w-7 text-white" />
              </motion.div>
              <h3 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">
                {isDragging ? 'Rilascia qui il file' : 'Trascina o clicca per caricare'}
              </h3>

              {capabilitiesError ? (
                <div className="mt-2 text-xs text-rose-600 dark:text-rose-400 max-w-md">
                  {capabilitiesError}
                </div>
              ) : capabilities ? (
                <>
                  <div className="mt-2">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border font-medium',
                        PIPELINE_BADGE[capabilities.pipelineLabel].className
                      )}
                      title={capabilities.pipelineTooltip}
                    >
                      <Sparkles className="h-3 w-3" />
                      {PIPELINE_BADGE[capabilities.pipelineLabel].label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-md">
                    {capabilities.pipelineTooltip}
                  </p>
                  {onNavigateToRouting && canNavigateToRouting && (
                    <button
                      type="button"
                      onClick={onNavigateToRouting}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 underline-offset-2 hover:underline"
                      title="Cambia provider/modello del tenant in Routing AI"
                    >
                      Cambia in Routing AI →
                    </button>
                  )}
                  <details className="mt-3 text-xs text-gray-600 dark:text-gray-400 max-w-md">
                    <summary className="cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100">
                      Tipologie supportate ({capabilities.acceptedExtensions.length})
                    </summary>
                    <div className="mt-2 space-y-1.5 text-left">
                      {capabilities.supportedKinds.map(group => (
                        <div key={group.label} className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-gray-700 dark:text-gray-300 mr-1">{group.label}:</span>
                          {group.extensions.map(ext => (
                            <span key={ext} className="text-[10px] px-1.5 py-0.5 rounded bg-white/80 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                              {ext}
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </details>
                </>
              ) : (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-md">
                  Caricamento capabilities…
                </p>
              )}
            </div>

            {/* Doc phase progress overlay */}
            {docPhase !== 'idle' && (
              <div className="border-t border-sky-200/70 dark:border-sky-800/50 bg-white/70 dark:bg-gray-900/70 backdrop-blur px-4 py-3">
                <div className="flex items-center gap-3">
                  {docPhase === 'complete' ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <Loader2 className="h-5 w-5 text-sky-500 animate-spin" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300 truncate">
                        {phaseLabels[docPhase]} {selectedFile && `· ${selectedFile.name}`}
                      </span>
                      <span className="text-gray-500 tabular-nums">{phaseProgress[docPhase]}%</span>
                    </div>
                    <Progress value={phaseProgress[docPhase]} className="h-1.5" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* URL ingest card */}
          <Card className="border-gray-200 dark:border-gray-700/50 overflow-hidden">
            <CardHeader className="py-3 bg-gradient-to-r from-emerald-50/60 to-transparent dark:from-emerald-950/20">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="h-4 w-4 text-emerald-600" />
                Aggiungi URL o sito
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-3">
              {firecrawlActive === true ? (
                <div className="flex items-start gap-2 text-xs bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 rounded-md px-3 py-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Firecrawl attivo</strong> — scraping avanzato con JS rendering abilitato.</span>
                </div>
              ) : firecrawlActive === false ? (
                <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300 rounded-md px-3 py-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Scraping base attivo</strong> — i siti che usano JavaScript potrebbero non essere estratti correttamente. <a href="/settings?tab=integrations" className="underline font-medium">Integra Firecrawl</a></span>
                </div>
              ) : null}

              {firecrawlActive === true && (
                <div className="space-y-2">
                  <div className="flex bg-gray-100 dark:bg-gray-800 rounded-md p-0.5 border border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      className={`flex-1 text-xs py-1.5 px-3 rounded transition-colors ${crawlMode === 'single' ? 'bg-white dark:bg-gray-700 shadow-sm font-medium text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                      onClick={() => setCrawlMode('single')}
                    >
                      Pagina singola
                    </button>
                    <button
                      type="button"
                      className={`flex-1 text-xs py-1.5 px-3 rounded transition-colors ${crawlMode === 'site' ? 'bg-white dark:bg-gray-700 shadow-sm font-medium text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                      onClick={() => setCrawlMode('site')}
                    >
                      Intero sito
                    </button>
                  </div>
                  {crawlMode === 'site' && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Max pagine:</Label>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        value={crawlMaxPages}
                        onChange={(e) => setCrawlMaxPages(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                        className="w-20 h-8 text-xs"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Task #1409 — toggle indicizzazione immagini scrape (Gemini-only).
                  Mostrato solo se l'embedder tenant è Gemini, altrimenti
                  irrilevante (richiede embedContent multimodale). */}
              <ScrapeMediaToggle />

              <div className="flex gap-2">
                <Input
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder={crawlMode === 'site' ? 'https://tuosito.com' : 'https://tuosito.com/pagina'}
                  disabled={isScrapingUrl || isUrlPreviewLoading || isCrawling}
                />
                {crawlMode === 'site' ? (
                  <Button onClick={crawlSiteWithProgress} disabled={isCrawling || !newUrl.trim()}>
                    {isCrawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                ) : (
                  <Button onClick={handlePreviewUrl} disabled={isUrlPreviewLoading || !newUrl.trim()} variant="outline">
                    {isUrlPreviewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  </Button>
                )}
              </div>

              {/* URL phase progress */}
              {urlPhase !== 'idle' && (
                <div className="rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-2">
                  <div className="flex items-center gap-3">
                    {urlPhase === 'complete' ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Loader2 className="h-4 w-4 text-emerald-500 animate-spin" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{phaseLabels[urlPhase]}</span>
                        <span className="text-gray-500 tabular-nums">{phaseProgress[urlPhase]}%</span>
                      </div>
                      <Progress value={phaseProgress[urlPhase]} className="h-1.5" />
                    </div>
                  </div>
                </div>
              )}

              {/* Crawl live progress */}
              {isCrawling && crawlProgress && (
                <div className="rounded-md border border-blue-200 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-950/20 px-3 py-2.5 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-blue-700 dark:text-blue-300 font-medium flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Crawl in corso · {crawlProgress.status}
                    </span>
                    <span className="text-blue-600 dark:text-blue-400 tabular-nums">
                      {crawlProgress.pagesProcessed}/{crawlProgress.pagesFound || '…'}
                    </span>
                  </div>
                  <Progress value={crawlProgress.pagesFound > 0 ? (crawlProgress.pagesProcessed / crawlProgress.pagesFound) * 100 : 10} className="h-1.5" />
                  <div className="flex items-center justify-between text-[11px] text-blue-600 dark:text-blue-400">
                    <span className="flex items-center gap-1"><Hash className="h-3 w-3" /> Chunks: {crawlProgress.totalChunks}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Filters / search bar */}
          {(documents.length > 0 || urls.length > 0) && (
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <div className="relative flex-1 min-w-0">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca per nome, URL o titolo..."
                  className="h-9 text-sm pl-8"
                />
                <FileText className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {(['all', 'documents', 'urls'] as const).map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => { setSourceFilter(opt); if (opt !== 'documents') setTypeFilter('all'); }}
                    className={cn(
                      'text-[11px] px-2.5 py-1 rounded-full border transition-colors',
                      sourceFilter === opt
                        ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-700'
                        : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                    )}
                  >
                    {opt === 'all' ? 'Tutto' : opt === 'documents' ? 'Documenti' : 'URL'}
                  </button>
                ))}
                {sourceFilter !== 'urls' && docTypeOptions.length > 0 && (
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="text-[11px] px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
                  >
                    <option value="all">Tutti i tipi</option>
                    {docTypeOptions.map(t => (
                      <option key={t} value={t}>{t.toUpperCase()}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          {/* Documents grid */}
          {filteredDocuments.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Documenti
                  <span className="text-xs font-normal text-gray-500">({filteredDocuments.length}/{documents.length})</span>
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {filteredDocuments.map((doc) => {
                  const ocr = doc.ocr_status;
                  const isPdf = (doc.file_type || '').includes('pdf') || (doc.filename || '').toLowerCase().endsWith('.pdf');
                  const isMultimodal = ocr === 'not_needed' && isPdf && (doc.chunk_count === 0 || doc.multimodal_bypass === true);
                  let ocrBadge: { label: string; className: string } | null = null;
                  if (ocr === 'pending') ocrBadge = { label: 'OCR in coda', className: 'bg-amber-100 text-amber-700 border-amber-300' };
                  else if (ocr === 'processing') ocrBadge = { label: 'OCR in corso…', className: 'bg-amber-100 text-amber-700 border-amber-300' };
                  else if (ocr === 'completed') ocrBadge = { label: 'OCR completato', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
                  else if (ocr === 'no_provider') ocrBadge = { label: 'OCR non configurato', className: 'bg-orange-100 text-orange-700 border-orange-300' };
                  else if (ocr === 'failed') ocrBadge = { label: 'OCR fallito', className: 'bg-red-100 text-red-700 border-red-300' };
                  else if (isMultimodal) ocrBadge = { label: 'Gemini multimodale', className: 'bg-blue-100 text-blue-700 border-blue-300' };

                  // Task #1287 — badge + progress pipeline embedding multimodale Gemini.
                  const embStatus = doc.embedding_status;
                  const embPct = Math.max(0, Math.min(100, doc.embedding_progress_pct ?? 0));
                  const embFailedPages = doc.embedding_failed_pages?.length ?? 0;
                  let embBadge: { label: string; className: string } | null = null;
                  if (embStatus === 'queued') embBadge = { label: 'Embedding in coda', className: 'bg-violet-100 text-violet-700 border-violet-300' };
                  else if (embStatus === 'processing') {
                    const tot = doc.embedding_total_pages ?? 0;
                    const proc = doc.embedding_processed_pages ?? 0;
                    embBadge = {
                      label: tot > 0 ? `Embedding ${proc}/${tot} pag (${embPct}%)` : `Embedding ${embPct}%`,
                      className: 'bg-violet-100 text-violet-700 border-violet-300',
                    };
                  } else if (embStatus === 'completed') embBadge = { label: 'Embedding completato', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
                  else if (embStatus === 'failed') embBadge = {
                    label: embFailedPages > 0 ? `Embedding parziale (${embFailedPages} pag falliti)` : 'Embedding fallito',
                    className: 'bg-orange-100 text-orange-700 border-orange-300',
                  };
                  else if (embStatus === 'dead_letter') embBadge = { label: 'Embedding bloccato (max retry)', className: 'bg-red-100 text-red-700 border-red-300' };

                  const showEmbProgress = embStatus === 'queued' || embStatus === 'processing';
                  const { Icon, tone } = fileIconFor(doc.filename, doc.file_type);
                  return (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="group relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 hover:shadow-sm hover:border-sky-300 dark:hover:border-sky-700 transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div className={cn('w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0', tone)}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={doc.filename}>
                            {doc.filename}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-0.5"><Hash className="h-3 w-3" />{doc.chunk_count}</span>
                            <span>·</span>
                            <span>{formatBytes(doc.file_size)}</span>
                            {doc.created_at && (
                              <>
                                <span>·</span>
                                <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{new Date(doc.created_at).toLocaleDateString()}</span>
                              </>
                            )}
                          </div>
                          {showEmbProgress && (
                            <div className="mt-1.5 h-1 w-full rounded-full bg-violet-100 dark:bg-violet-900/30 overflow-hidden">
                              <div
                                className="h-full bg-violet-500 transition-all duration-500"
                                style={{ width: `${embStatus === 'queued' ? 4 : embPct}%` }}
                              />
                            </div>
                          )}
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            {ocrBadge && <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${ocrBadge.className}`}>{ocrBadge.label}</Badge>}
                            {embBadge && <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${embBadge.className}`} title={doc.embedding_last_error || undefined}>{embBadge.label}</Badge>}
                            {ocr === 'no_provider' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-5 px-1.5 text-[10px] border-amber-400 text-amber-700 hover:bg-amber-50"
                                onClick={() => {
                                  try {
                                    const url = new URL(window.location.href);
                                    url.searchParams.set('tab', 'routing');
                                    url.searchParams.set('focus', 'ocr');
                                    window.history.replaceState({}, '', url.toString());
                                  } catch { /* ignore */ }
                                  window.dispatchEvent(new CustomEvent('ai-tools:focus-service', { detail: { tab: 'routing', focusService: 'ocr' } }));
                                }}
                              >
                                Configura OCR
                              </Button>
                            )}
                          </div>
                          {doc.error_message && (ocr === 'failed' || ocr === 'no_provider') && (
                            <p className="mt-1 text-[10px] text-red-500 truncate" title={doc.error_message}>{doc.error_message.slice(0, 80)}</p>
                          )}
                          {/* Task #1307 — re-ingest visibile per doc multimodali quando chat model non è Gemini */}
                          {capabilities && !capabilities.chatModel.isGemini && isMultimodal && (
                            <div className="mt-1.5">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-[10px] border-amber-400 text-amber-700 hover:bg-amber-50"
                                disabled={reingestingDocId === doc.id}
                                onClick={() => handleReingestDoc(doc.id)}
                                title="Resetta documento per re-upload con nuovo embedder/chat model"
                              >
                                {reingestingDocId === doc.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                ) : (
                                  <RefreshCw className="h-3 w-3 mr-1" />
                                )}
                                Re-ingest
                              </Button>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 flex-shrink-0"
                          onClick={() => setEditMetaTarget({
                            kind: 'document',
                            id: doc.id,
                            label: doc.filename,
                            meta: {
                              language: doc.language ?? null,
                              audience: doc.audience ?? [],
                              sensitivity: doc.sensitivity ?? null,
                            },
                          })}
                          title="Modifica metadata"
                        >
                          <Pencil className="h-3.5 w-3.5 text-indigo-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 flex-shrink-0"
                          onClick={() => handleDeleteDocument(doc.id)}
                          title="Elimina"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </Button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* URLs grid */}
          {filteredUrls.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" /> URL
                  <span className="text-xs font-normal text-gray-500">({filteredUrls.length}/{urls.length})</span>
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {filteredUrls.map((url) => {
                  const fav = faviconFor(url.url);
                  const host = hostnameOf(url.url);
                  const lastCrawled = url.last_crawled_at || url.processed_at || url.created_at;
                  const isReindexing = reindexingUrlId === url.id;
                  return (
                    <motion.div
                      key={url.id}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="group relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3 hover:shadow-sm hover:border-emerald-300 dark:hover:border-emerald-700 transition-all cursor-pointer"
                      onClick={() => handlePreviewExistingUrl(url)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 bg-emerald-50 dark:bg-emerald-900/20 overflow-hidden">
                          {fav ? (
                            <img src={fav} alt="" className="w-6 h-6" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          ) : (
                            <Globe className="h-5 w-5 text-emerald-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={url.title || url.url}>
                            {url.title || host}
                          </div>
                          <a
                            href={url.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline truncate block"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {url.url}
                          </a>
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] py-0 px-1.5',
                                url.status === 'completed'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                  : url.status === 'error'
                                  ? 'bg-red-50 text-red-700 border-red-300'
                                  : 'bg-amber-50 text-amber-700 border-amber-300'
                              )}
                            >
                              {url.status === 'completed' ? `${url.chunk_count} chunks` : url.status}
                            </Badge>
                            {url.language && (
                              <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-blue-50 text-blue-700 border-blue-300">
                                {url.language.toUpperCase()}
                              </Badge>
                            )}
                            {lastCrawled && (
                              <span className="text-[10px] text-gray-500 flex items-center gap-0.5" title={`Ultimo crawl: ${new Date(lastCrawled).toLocaleString()}`}>
                                <Clock className="h-3 w-3" />{new Date(lastCrawled).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {url.error_message && url.status !== 'completed' && (
                            <p className="mt-1 text-[10px] text-red-500 truncate" title={url.error_message}>{url.error_message.slice(0, 80)}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); handleReindexUrl(url); }}
                            disabled={isReindexing}
                            title="Reindex"
                          >
                            {isReindexing
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-500" />
                              : <RefreshCw className="h-3.5 w-3.5 text-emerald-500" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditMetaTarget({
                                kind: 'url',
                                id: url.id,
                                label: url.title || url.url,
                                meta: {
                                  language: url.language ?? null,
                                  audience: url.audience ?? [],
                                  sensitivity: url.sensitivity ?? null,
                                },
                              });
                            }}
                            title="Modifica metadata"
                          >
                            <Pencil className="h-3.5 w-3.5 text-indigo-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => { e.stopPropagation(); handleDeleteUrl(url.id); }}
                            title="Elimina"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {documents.length === 0 && urls.length === 0 && (
            <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500">
              Nessuna fonte ancora indicizzata. Carica un file o aggiungi un URL per iniziare.
            </div>
          )}
          {(documents.length > 0 || urls.length > 0) && filteredDocuments.length === 0 && filteredUrls.length === 0 && (
            <div className="text-center py-6 text-xs text-gray-500 dark:text-gray-400">
              Nessun risultato per i filtri attuali.
              <button onClick={() => { setSearchQuery(''); setSourceFilter('all'); setTypeFilter('all'); }} className="ml-2 underline text-sky-600">Reset</button>
            </div>
          )}
        </div>

        {/* RIGHT column: side panel preview */}
        <AnimatePresence>
          {activePreview && (
            <motion.aside
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.18 }}
              className="lg:sticky lg:top-2 self-start rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden shadow-sm"
            >
              <div className={cn(
                'px-4 py-3 flex items-center justify-between border-b',
                previewKind === 'doc'
                  ? 'border-sky-200 dark:border-sky-900 bg-gradient-to-r from-sky-50 to-purple-50 dark:from-sky-950/40 dark:to-purple-950/40'
                  : 'border-emerald-200 dark:border-emerald-900 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40'
              )}>
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles className={cn('h-4 w-4 flex-shrink-0', previewKind === 'doc' ? 'text-sky-600' : 'text-emerald-600')} />
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">Anteprima</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={chunkPreview?.filename || urlPreview?.title}>
                      {chunkPreview?.filename || selectedFile?.name || urlPreview?.title || 'Senza titolo'}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 flex-shrink-0"
                  onClick={previewKind === 'doc' ? handleCancelUpload : handleCancelScrape}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="p-4 space-y-3">
                {urlPreview && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Nome auto: <span className="text-emerald-600 dark:text-emerald-400 font-medium">{urlPreview.auto_name}</span>
                  </p>
                )}

                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-2 text-center">
                    <div className="text-lg font-bold text-sky-600 tabular-nums">{activePreview.chunk_count}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Chunks</div>
                  </div>
                  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-2 text-center">
                    <div className="text-lg font-bold text-purple-600 tabular-nums">{activePreview.total_tokens}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tokens</div>
                  </div>
                  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-2 text-center">
                    <div className="text-lg font-bold text-emerald-600 tabular-nums">{activePreview.avg_tokens_per_chunk}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Avg/Chunk</div>
                  </div>
                </div>

                {!compact && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                      Anteprima chunks
                    </div>
                    <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                      {activePreview.chunks.slice(0, 6).map((chunk) => (
                        <div key={chunk.index} className="text-[11px] bg-gray-50 dark:bg-gray-800/60 p-2 rounded border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-gray-400 dark:text-gray-500 font-mono">#{chunk.index + 1}</span>
                            <span className="text-gray-400 dark:text-gray-500">{chunk.token_count} tok</span>
                          </div>
                          <div className="text-gray-700 dark:text-gray-300 line-clamp-3">{chunk.preview}</div>
                        </div>
                      ))}
                      {activePreview.chunks.length > 6 && (
                        <p className="text-[10px] text-gray-400 italic text-center">+ altri {activePreview.chunks.length - 6} chunks…</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={previewKind === 'doc' ? handleCancelUpload : handleCancelScrape}
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Annulla
                  </Button>
                  {previewKind === 'doc' ? (
                    <Button size="sm" className="flex-1" onClick={handleConfirmUpload} disabled={isLoading}>
                      {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                      Indicizza
                    </Button>
                  ) : (
                    <Button size="sm" className="flex-1" onClick={handleConfirmScrape} disabled={isScrapingUrl}>
                      {isScrapingUrl ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                      Indicizza
                    </Button>
                  )}
                </div>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Task #1515 — Modal modifica metadata documento/URL esistente. */}
      <Dialog open={editMetaTarget !== null} onOpenChange={(open) => { if (!open) setEditMetaTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Modifica metadata · {editMetaTarget?.label ?? ''}
            </DialogTitle>
          </DialogHeader>
          {editMetaTarget && (
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label className="text-xs">Lingua</Label>
                <select
                  value={editMetaTarget.meta.language ?? ''}
                  onChange={(e) => setEditMetaTarget((t) => t ? ({ ...t, meta: { ...t.meta, language: e.target.value || null } }) : t)}
                  className="w-full h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-background px-2 text-sm"
                >
                  <option value="">— invariata —</option>
                  {kbOptsByDim('language').map((o) => (
                    <option key={o.id} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Audience (multi)</Label>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {kbOptsByDim('audience').map((o) => {
                    const checked = editMetaTarget.meta.audience.includes(o.value);
                    return (
                      <button
                        type="button"
                        key={o.id}
                        onClick={() => setEditMetaTarget((t) => t ? ({
                          ...t,
                          meta: {
                            ...t.meta,
                            audience: checked
                              ? t.meta.audience.filter((v) => v !== o.value)
                              : [...t.meta.audience, o.value],
                          },
                        }) : t)}
                        className={cn(
                          'text-[11px] px-2 py-1 rounded-full border transition-colors',
                          checked
                            ? 'bg-indigo-500 border-indigo-500 text-white'
                            : 'bg-background border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-indigo-400'
                        )}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Sensitività</Label>
                <select
                  value={editMetaTarget.meta.sensitivity ?? ''}
                  onChange={(e) => setEditMetaTarget((t) => t ? ({ ...t, meta: { ...t.meta, sensitivity: e.target.value || null } }) : t)}
                  className="w-full h-9 rounded-md border border-gray-200 dark:border-gray-700 bg-background px-2 text-sm"
                >
                  <option value="">— invariata —</option>
                  {kbOptsByDim('sensitivity').map((o) => (
                    <option key={o.id} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <p className="text-[11px] text-gray-500">
                I metadata vengono propagati a tutti i chunk indicizzati di questa sorgente.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMetaTarget(null)} disabled={savingEditMeta}>
              Annulla
            </Button>
            <Button
              disabled={savingEditMeta || !editMetaTarget}
              onClick={async () => {
                if (!editMetaTarget) return;
                setSavingEditMeta(true);
                try {
                  const sourceType = editMetaTarget.kind === 'document' ? 'document' : 'url';
                  const res = await apiClient.patch<{ success: boolean; error?: string }>(
                    `${basePath}/sources/${sourceType}/${editMetaTarget.id}/metadata`,
                    {
                      language: editMetaTarget.meta.language,
                      audience: editMetaTarget.meta.audience,
                      sensitivity: editMetaTarget.meta.sensitivity,
                    }
                  );
                  if (!res.success) throw new Error(res.error || 'Errore aggiornamento');
                  toast({ title: 'Metadata aggiornati' });
                  setEditMetaTarget(null);
                  loadKnowledgeBase();
                } catch (err) {
                  const msg = err instanceof Error ? err.message : 'Errore';
                  toast({ title: 'Errore', description: msg, variant: 'destructive' });
                } finally {
                  setSavingEditMeta(false);
                }
              }}
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
